<template>
  <v-card
    v-if="fields.length > 0"
    :flat="flat"
  >
    <v-card-title>
      <v-form @submit.prevent="submit">
        <v-layout
          flex
          wrap
          :row="!directionIsColumn"
          :column="directionIsColumn"
        >
          <template v-for="field in fields">
            <v-flex
              :key="field.name"
              v-bind="getLayoutByName(field.name)"
              @keydown.13="onkeydown"
            >
              <component
                :is="getComponentByField(field.name)"
                :value="getDirtyValueByName(field.name)"
                :hide-details="hideDetails"
                :direction="direction"
                :project="getDirtyValueByName('project')"
                :loading="loading"
                :disabled="loading || saving || disabled"
                @input="setDirtyValueByName(field.name, $event)"
                @keydown="onkeydown($event)"
              />
            </v-flex>
          </template>
        </v-layout>
      </v-form>
    </v-card-title>
    <v-card-actions v-show="!hideActions">
      <v-btn
        v-if="showDeleteButton"
        :disabled="disabled || saving"
        color="error"
        text
        @click="$emit('delete')"
      >
        Löschen
      </v-btn>
      <v-spacer />
      <v-progress-circular
        v-if="saving"
        indeterminate
      />
      <v-btn
        v-if="showCancelButton"
        :disabled="disabled || saving"
        color="secondary"
        text
        @click="$emit('cancel')"
      >
        Abbrechen
      </v-btn>
      <v-btn
        color="primary"
        text
        :disabled="disabled || saving"
        @click="submit"
      >
        Speichern
      </v-btn>
    </v-card-actions>
  </v-card>
  <v-progress-linear
    v-else
    indeterminate
  />
</template>

<script>
import moment from 'moment'

import { mapGetters } from 'vuex'
import { api, endpoints } from '@/api'

import DateInput from '../selects/Date'
import TaskDurationInput from '../selects/TaskDuration'
import JourneyDurationInput from '../selects/JourneyDuration'
import JourneyInput from '../selects/Journey'
import PlaceInput from '../selects/Place'
import DescriptionInput from '../selects/Description'
import AdditionalMealAllowanceInput from '../selects/AdditionalMealAllowance'

import ProjectInput from '../selects/Project'
import EmployeeInput from '../selects/Employee'
import OperationInput from '../selects/Operation'


export default {
  components: {
    'date-input': DateInput,
    'task-duration-input': TaskDurationInput,
    'journey-duration-input': JourneyDurationInput,
    'project-input': ProjectInput,
    'employee-input': EmployeeInput,
    'place-input': PlaceInput,
    'journey-input': JourneyInput,
    'operation-input': OperationInput,
    'description-input': DescriptionInput,
    'additional-meal-allowance-input': AdditionalMealAllowanceInput
  },
  props: {
    log: {
      type: Object,
      default: () => null,
    },
    direction: {
      type: String,
      default: 'row',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearOnSave: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    showCancelButton: {
      type: Boolean,
      default: false,
    },
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const result = {
      dirtyValues: this.getCleanDirtyValues(),
      hideDetails: true,
      loading: false,
      saving: false,
      employeeUntouched: true,
    }
    return result
  },
  computed: {
    ...mapGetters([
      'selectedTenantObj',
      'profile',
      'employee',
    ]),
    directionIsColumn () {
      return this.direction === 'column'
    },
    descriptionRows () {
      return (this.directionIsColumn ? 4 : 1)
    },
    fields () {
      return (this.selectedTenantObj && this.selectedTenantObj.log_fields && this.selectedTenantObj.log_fields.edit) || []
    }
  },
  watch: {
    log: {
      handler () {
        this.updateEdit()
      },
      immediate: true,
    },
    employee: {
      handler (v) {
        if (this.employeeUntouched && this.log === null) {
          this.dirtyValues.employee = v
        }
      },
      immediate: true,
    },
  },
  methods: {
    onkeydown (e) {
      if (e !== undefined && e.key === 'Enter' && e.ctrlKey === true){
        this.submit()
      }
    },
    async submit () {
      const dv = this.dirtyValues
      const obj = {
        additional_meal_allowance: String(dv.additional_meal_allowance || 0),
        description: dv.description || '',
        employee: dv.employee ? dv.employee.id : null,
        journey: dv.journey || 0,
        journey_duration: dv.journey_duration || 0,
        operation: dv.operation ? dv.operation.id : null,
        place: dv.place || 0,
        project: dv.project ? dv.project.id : null,
        taskday: dv.taskday.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD'),
        taskduration: dv.taskduration || 0,
      }

      var translated = {
        'employee': 'Mitarbeiter fehlt!',
        'project': 'Projekt fehlt!'
      }

      this.$store.dispatch('clearAlerts')
      if (this.log) {
        obj.id = this.log.id
      }
      let endpoint, method
      if (obj.id) {
        endpoint = endpoints.fetch_log(obj.id)
        method = 'PUT'
      } else {
        endpoint = endpoints.fetch_log()
        method = 'POST'
      }
      this.saving = true
      try {
        const r = await api(endpoint, obj, {method})
        if (r.ok) {
          this.$emit('saved', await r.json())
            if (this.clearOnSave) {
              this.clear()
            }
            this.$root.$emit("logsChanged", obj)
        } else {
          // TODO: error handling
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    getCleanDirtyValues () {
      return {
        taskday: moment(),
        employee: this.employee,
        project: null,
        operation: null,
        taskduration: 15,
        journey_duration: 0,
        additional_meal_allowance: 0,
        place: null,
        journey: null,
        description: null
      }
    },
    clear () {
      const cleared = {...this.getCleanDirtyValues()}
      if (this.profile.keep_employee_in_edit) {
        cleared.employee = this.dirtyValues.employee !== null ? {...this.dirtyValues.employee} : null
      }
      if (this.profile.keep_project_in_edit) {
        cleared.project = this.dirtyValues.project !== null ? {...this.dirtyValues.project} : null
      }
      if (this.profile.increment_taskday_on_save){
        var d = this.dirtyValues.taskday.clone()
        cleared.taskday = d.add(1, 'day')
      }
      this.$set(this, 'dirtyValues', cleared)
      this.employeeUntouched = true
    },
    getComponentByField (fieldName) {
      switch (fieldName) {
        case 'taskday':
          return 'date-input'
        case 'employee':
          return 'employee-input'
        case 'project':
          return 'project-input'
        case 'taskduration':
          return 'task-duration-input'
        case 'journey_duration':
          return 'journey-duration-input'
        case 'journey':
          return 'journey-input'
        case 'place':
          return 'place-input'
        case 'operation':
          return 'operation-input'
        case 'description':
          return 'description-input'
        case 'additional_meal_allowance':
          return 'additional-meal-allowance-input'
        default:
          return 'span'
      }
    },
    getDirtyValueByName (fieldName) {
      return this.dirtyValues[fieldName]
    },
    setDirtyValueByName (fieldName, value) {
      if (fieldName === 'employee') {
        this.employeeUntouched = false
        this.$emit("employeeChange", value)
      }
      this.dirtyValues[fieldName] = value
    },
    getLayoutByName (fieldName) {
      for (let i = 0; i < this.fields.length; i++) {
        if (this.fields[i].name === fieldName) {
          const result = {}
          for (let j = 0; j < this.fields[i].layout.length; j++) {
            result[this.fields[i].layout[j]] = true
          }
          return result
        }
      }
      return {}
    },
    updateEdit () {
      if (this.log) {
        this.$set(this, 'dirtyValues', {
          taskday: moment(this.log.taskday, 'YYYY-MM-DD'),
          description: this.log.description,
          journey: this.log.journey,
          place: this.log.place,
          taskduration: this.log.taskduration,
          journey_duration: this.log.journey_duration,
          additional_meal_allowance: Number(this.log.additional_meal_allowance),
          project: this.log.project,
          employee: this.log.employee,
          operation: this.log.operation,
        })
      }
    },
  }
}
</script>

<style lang="scss" scoped>
form {
  width: 100%;
  display: inline-block;
  padding-bottom: 5px;
}

.row {
  margin: 0 0 4px 0;
  display: flex;
  width: 100%;
  .form-control {
    border-radius: 0;
    height: 40px;
  }
  .col-1, .col-2, .col-3, .col-4, .col-5, .col-6, .col-7, .col-8, .col-9, .col-10, .col-11, .col-12, .col, .col-auto, .col-sm-1, .col-sm-2, .col-sm-3, .col-sm-4, .col-sm-5, .col-sm-6, .col-sm-7, .col-sm-8, .col-sm-9, .col-sm-10, .col-sm-11, .col-sm-12, .col-sm, .col-sm-auto, .col-md-1, .col-md-2, .col-md-3, .col-md-4, .col-md-5, .col-md-6, .col-md-7, .col-md-8, .col-md-9, .col-md-10, .col-md-11, .col-md-12, .col-md, .col-md-auto, .col-lg-1, .col-lg-2, .col-lg-3, .col-lg-4, .col-lg-5, .col-lg-6, .col-lg-7, .col-lg-8, .col-lg-9, .col-lg-10, .col-lg-11, .col-lg-12, .col-lg, .col-lg-auto, .col-xl-1, .col-xl-2, .col-xl-3, .col-xl-4, .col-xl-5, .col-xl-6, .col-xl-7, .col-xl-8, .col-xl-9, .col-xl-10, .col-xl-11, .col-xl-12, .col-xl, .col-xl-auto {
    padding: 0 2px 2px 2px;
  }
  .button {
    width: 100%;
    .btn {
      width: 100%;
      text-align: center;
      padding: 0;
    }
  }
}

.column {
  .col-1, .col-2, .col-3, .col-4, .col-5, .col-6, .col-7, .col-8, .col-9, .col-10, .col-11, .col-12, .col, .col-auto, .col-sm-1, .col-sm-2, .col-sm-3, .col-sm-4, .col-sm-5, .col-sm-6, .col-sm-7, .col-sm-8, .col-sm-9, .col-sm-10, .col-sm-11, .col-sm-12, .col-sm, .col-sm-auto, .col-md-1, .col-md-2, .col-md-3, .col-md-4, .col-md-5, .col-md-6, .col-md-7, .col-md-8, .col-md-9, .col-md-10, .col-md-11, .col-md-12, .col-md, .col-md-auto, .col-lg-1, .col-lg-2, .col-lg-3, .col-lg-4, .col-lg-5, .col-lg-6, .col-lg-7, .col-lg-8, .col-lg-9, .col-lg-10, .col-lg-11, .col-lg-12, .col-lg, .col-lg-auto, .col-xl-1, .col-xl-2, .col-xl-3, .col-xl-4, .col-xl-5, .col-xl-6, .col-xl-7, .col-xl-8, .col-xl-9, .col-xl-10, .col-xl-11, .col-xl-12, .col-xl, .col-xl-auto {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 0 2px 0 !important;

  }
  &:last-child {
    padding: 0;
    width: 100% !important;
  }
  textarea {
    height: 70px !important;
    width: 100%;
  }
  .text, .col-sm-9, .col-md-9, .col-lg-10, .col-xl-10 {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    padding: 0;
  }
  .button {
    padding-right: 0 !important;
    display: block;
    height: 40px;
    margin-top: 5px;
    width: 100%;
    max-width: 100%;
  }
}

input {
  padding: 9px 0 10px 10px !important;
}

textarea, input {
  border: 1px solid #e8e8e8;
  padding: 5px 10px 0 10px;
  min-height: 40px !important;
  font-size: 13px;
}

textarea {
  resize: vertical;
  overflow: none;
  width: 100%;
  padding-top: 9px;
}

.save {
  float: right;
  height: 40px;
  display: block;
}
</style>
